import React, { useEffect, useState } from 'react'
import { useForm, Controller, FormProvider } from 'react-hook-form'
import {
    Accordion as MuiAccordion,
    AccordionDetails,
    AccordionSummary,
    Grid,
    LinearProgress,
    TextField,
    Typography,
    Autocomplete,
    Button,
    CircularProgress,
} from '@mui/material'
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import useAuth from '../../../hooks/useAuth'
import styled from '@emotion/styled'

// Issue form components
import { RegisteredIssueTypeSelect } from '../../../components/entities/IssueType'
import { formatDateYYYYMMDD } from '../../../packages/utils/numberFormatters'
import IpoFields from './AdminIpoFields'
import IssueApi from '../../../api/IssueApi'
import ShareApi from '../../../api/ShareApi'

const Accordion = styled((props) => <MuiAccordion disableGutters elevation={0} square {...props} />)(({ theme }) => ({
    border: `1px solid ${theme.palette.divider}`,
    '&:not(:last-child)': {
        borderBottom: 0,
    },
    '&::before': {
        display: 'none',
    },
}))

function FormAccordionSummary({ children }) {
    return (
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant='h6'>{children}</Typography>
        </AccordionSummary>
    )
}

const shareApi = new ShareApi()

function ShareSelect() {
    const [shares, setShares] = useState([])
    const { isAuthenticated } = useAuth()
    useEffect(() => {
        if (!isAuthenticated) return
        async function fetchData() {
            let newShares = await shareApi.findAll()

            newShares = newShares.map((share) => {
                if (share.delistedDate) {
                    share.name = `${share.name} (avnoterat ${formatDateYYYYMMDD(share.delistedDate)})`
                }
                return share
            })
            // sort by name
            newShares.sort((a, b) => a.name.localeCompare(b.name))
            setShares(newShares)
        }
        fetchData()
    }, [isAuthenticated])

    if (!shares || shares.length === 0) return <LinearProgress />

    return (
        <Controller
            name='share'
            defaultValue={null}
            render={({ field: { onChange, onBlur, value } }) => {
                return (
                    <Autocomplete
                        fullWidth
                        options={shares}
                        getOptionLabel={(option) => option.name}
                        value={value}
                        onBlur={onBlur}
                        onChange={(event, newValue) => {
                            onChange(newValue)
                        }}
                        renderInput={(params) => <TextField {...params} label='Aktie' />}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                )
            }}
        />
    )
}

function AdminCreateIssueForm() {
    const navigate = useNavigate()
    const formMethods = useForm()
    const [creating, setCreating] = useState(false)
    const issueApi = new IssueApi()

    const getIssueToSave = (data) => {
        if (issueType === 'IPO') {
            const { ipoData } = data
            if (share && !newInstrument) {
                return { shareId: data.share.id, type: data.type }
            }
            return {
                type: data.type,
                ipoData: {
                    instrumentName: ipoData.instrumentName,
                },
            }
        } else {
            return { shareId: data.share.id, type: data.type }
        }
    }

    const handleSave = async (data) => {
        if (creating) return
        setCreating(true)

        const issueToSave = getIssueToSave(data)

        const result = await issueApi.create(issueToSave)
        if (result) {
            // navigate to the new issue
            navigate(`/admin/issues/${result.id}`)
        } else {
            console.log('error result', JSON.stringify(result, null, 2))
            setCreating(false)
        }
    }

    const issueType = formMethods.watch('type')
    const share = formMethods.watch('share')
    const newInstrument = formMethods.watch('newInstrument')

    const ipoNewCompanyFieldsInvalid = !share && !formMethods.watch('ipoData.instrumentName')

    const ipoExistingCompanyFieldsInvalid = share && newInstrument && !formMethods.watch('ipoData.instrumentName')

    const formFieldsInvalid = issueType === 'IPO' && (ipoNewCompanyFieldsInvalid || ipoExistingCompanyFieldsInvalid)

    return (
        <FormProvider {...formMethods}>
            <form id='issueform' onSubmit={formMethods.handleSubmit(handleSave)}>
                <Grid sx={{ mb: 2 }} container spacing={2} direction='row'>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <ShareSelect />
                    </Grid>
                    <Grid size={{ xs: 12, md: 5 }}>
                        <RegisteredIssueTypeSelect fullWidth />
                    </Grid>

                    <Grid size={{ xs: 12, md: 1, mt: 2 }}>
                        <Button
                            variant='contained'
                            color='primary'
                            fullWidth
                            type='submit'
                            disabled={formFieldsInvalid || creating}
                        >
                            Skapa {creating && <CircularProgress size={24} />}
                        </Button>
                    </Grid>
                </Grid>
                {issueType === 'IPO' ? (
                    <Accordion defaultExpanded>
                        <FormAccordionSummary>Bolag och aktie</FormAccordionSummary>
                        <AccordionDetails>
                            <IpoFields newIpo share={share} />
                        </AccordionDetails>
                    </Accordion>
                ) : null}
            </form>
        </FormProvider>
    )
}

export default AdminCreateIssueForm
